import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Award, Download, Plus } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl, extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

export default function Certificates() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : i18n.language === "pt" ? "pt-BR" : "es-ES";
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [funds, setFunds] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", amount: "", currency: "USD", modality: "", description: "", fund_id: "" });

  const load = async () => {
    const { data } = await api.get("/api/certificates/mine");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  // Sync default modality with current language.
  useEffect(() => {
    setForm((f) => ({ ...f, modality: f.modality || t("certificates.modality_default") }));
  }, [t]);

  useEffect(() => {
    if (user?.role !== "sponsor") return;
    (async () => {
      const [{ data: projs }, { data: fs }] = await Promise.all([
        api.get("/api/projects?only_published=true&limit=200"),
        api.get("/api/funds"),
      ]);
      setProjects(projs);
      setFunds(fs);
    })();
  }, [user]);

  const issue = async () => {
    if (!form.project_id) return toast.error(t("certificates.toast_project_required"));
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error(t("certificates.toast_amount_invalid"));
    try {
      const payload = {
        project_id: form.project_id,
        amount: parseFloat(form.amount),
        currency: form.currency,
        modality: form.modality,
        description: form.description,
      };
      if (form.fund_id) payload.fund_id = form.fund_id;
      await api.post("/api/certificates", payload);
      toast.success(t("certificates.toast_issued"));
      setOpen(false);
      setForm({ project_id: "", amount: "", currency: "USD", modality: t("certificates.modality_default"), description: "", fund_id: "" });
      load();
    } catch (e) { toast.error(extractErrorMessage(e)); }
  };

  const download = (id) => {
    const url = apiUrl(`/api/certificates/${id}/pdf`);
    window.open(url, "_blank");
  };

  return (
    <DashboardLayout
      title={t("certificates.title")}
      subtitle={t("certificates.subtitle")}
      actions={
        user?.role === "sponsor" ? (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="button-issue-certificate" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
                <Plus size={16} className="mr-2" /> {t("certificates.issue_button")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{t("certificates.issue_dialog_title")}</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div>
                  <Label>{t("certificates.label_project")}</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="select-cert-project"><SelectValue placeholder={t("certificates.select_project_placeholder")} /></SelectTrigger>
                    <SelectContent>
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t("certificates.label_amount")}</Label>
                    <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="input-cert-amount" />
                  </div>
                  <div>
                    <Label>{t("certificates.label_currency")}</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["USD", "EUR", "CLP", "MXN", "ARS", "COP", "PEN", "BRL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>{t("certificates.label_modality")}</Label>
                  <Input value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} />
                </div>
                <div>
                  <Label>{t("certificates.label_fund")}</Label>
                  <Select value={form.fund_id || "none"} onValueChange={(v) => setForm({ ...form, fund_id: v === "none" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder={t("certificates.fund_none_placeholder")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("certificates.fund_none")}</SelectItem>
                      {funds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name} ({f.country})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("certificates.label_description")}</Label>
                  <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="textarea-cert-description" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={issue} data-testid="button-confirm-issue" className="bg-petroleum text-white hover:bg-petroleum/90">{t("certificates.issue_confirm")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        ) : null
      }
    >
      {items.length === 0 ? (
        <div className="kpi-card p-10 text-center">
          <Award size={28} className="text-gold mx-auto" />
          <h3 className="font-display text-2xl text-petroleum mt-3">{t("certificates.empty_title")}</h3>
          <p className="text-slate-500 mt-2">
            {user?.role === "sponsor" ? t("certificates.empty_sponsor") : t("certificates.empty_creator")}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((c) => (
            <div key={c.id} className="kpi-card p-5" data-testid={`certificate-${c.id}`}>
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-gold/15 text-gold hover:bg-gold/15 border-0 uppercase tracking-widest text-[10px]">{c.reference}</Badge>
                    <Badge variant="secondary">{c.role === "sponsor" ? t("certificates.role_sponsor") : t("certificates.role_creator")}</Badge>
                  </div>
                  <h3 className="font-display text-xl text-petroleum mt-2">{c.project?.title || t("certificates.project_default")}</h3>
                  <p className="text-sm text-slate-500 mt-1">{c.modality}</p>
                  {c.description && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{c.description}</p>}
                  <p className="text-xs text-slate-400 mt-2">{t("certificates.issued_at", { date: new Date(c.issued_at).toLocaleDateString(dateLocale) })}</p>
                </div>
                <div className="text-right space-y-2">
                  <p className="font-display text-2xl text-petroleum">{c.currency} {c.amount.toLocaleString()}</p>
                  <Button onClick={() => download(c.id)} variant="outline" data-testid={`button-download-cert-${c.id}`} className="border-slate-300">
                    <Download size={14} className="mr-2" /> PDF
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
